import {
  buildCrmTaskActionGroups,
  buildCrmTaskContactChannelActionGroup,
  type CrmTaskAction,
  type CrmTaskActionGroup,
} from "@ease/crm/task-action-groups";
import { shouldOpenCrmActionHrefInNewTab } from "@ease/crm/safe-action-hrefs";
import {
  buildCrmTaskProfileSections,
  summarizeCrmTaskProfileSections,
  type CrmTaskProfileSectionTone,
} from "@ease/crm/task-profile-sections";
import { Skeleton } from "@/components/skeleton";
import type { TaskSummary } from "@/hooks/use-tasks";
import { formatDate } from "@/lib/i18n";

type TaskSignalTone = "default" | "success" | "warning" | "danger" | "info";

interface RelatedTaskSnapshotCardProps {
  title: string;
  description: string;
  tasks: TaskSummary[];
  openTaskCount: number;
  isLoading: boolean;
  error: string | null;
  viewAllHref: string;
  emptyMessage: string;
  overflowMessage: (visibleCount: number, totalCount: number) => string;
  evidenceLabel: string;
}

const TASK_ACTION_CLASS: Record<CrmTaskAction["tone"], string> = {
  neutral: "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
  primary: "border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 text-[var(--color-primary)]",
  success: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  warning: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  danger: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

export function RelatedTaskSnapshotCard({
  title,
  description,
  tasks,
  openTaskCount,
  isLoading,
  error,
  viewAllHref,
  emptyMessage,
  overflowMessage,
  evidenceLabel,
}: RelatedTaskSnapshotCardProps) {
  return (
    <div className="card-hover rounded-lg border border-[var(--color-border)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            {title}
          </h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
            {openTaskCount}
          </span>
          <a
            href={viewAllHref}
            className="rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]"
          >
            View all
          </a>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : error ? (
        <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
          Task snapshot is temporarily unavailable.
        </p>
      ) : tasks.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">{emptyMessage}</p>
      ) : (
        <div className="mt-3 space-y-3">
          {tasks.map((task) => (
            <RelatedTaskSnapshotRow key={task.id} evidenceLabel={evidenceLabel} task={task} />
          ))}
          {openTaskCount > tasks.length ? (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {overflowMessage(tasks.length, openTaskCount)}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function RelatedTaskSnapshotRow({
  task,
  evidenceLabel,
}: {
  task: TaskSummary;
  evidenceLabel: string;
}) {
  const label = task.operatorReadModel?.labels.type ?? formatTaskLabel(task.type);
  const priority = task.operatorReadModel?.labels.priority ?? formatTaskLabel(task.priority);
  const status = task.operatorReadModel?.labels.status ?? formatTaskLabel(task.status);
  const taskMeta = [
    label,
    priority,
    status,
    task.dueAt ? `Due ${formatDate(task.dueAt)}` : null,
  ].filter(Boolean).join(" · ");
  const guidance = task.operatorReadModel?.guidance
    ?? task.operatorReadModel?.contextSummary
    ?? task.description
    ?? null;
  const contextSignal = getTaskContextSignal(task);
  const slaSignal = task.operatorReadModel?.sla ? getTaskSlaSignal(task.operatorReadModel.sla) : null;
  const completionSummary = getTaskCompletionSummary(task);
  const briefEntries = task.operatorReadModel?.operatorBrief?.slice(0, 3) ?? [];
  const escalation = task.operatorReadModel?.escalation ?? null;
  const automationResolution = task.operatorReadModel?.automationResolution ?? null;
  const automationWatch = getTaskAutomationWatch(task);
  const taskEvidenceRecords = [task.metadata, task.contextSnapshot];
  const evidenceSummary = summarizeCrmTaskProfileSections(
    buildCrmTaskProfileSections(taskEvidenceRecords, { maxSections: 3, maxItemsPerSection: 5 }),
  );
  const taskActionGroups = [
    buildCrmTaskContactChannelActionGroup(taskEvidenceRecords, { maxActions: 4 }),
    ...buildCrmTaskActionGroups(taskEvidenceRecords, { maxGroups: 3, maxActionsPerGroup: 4 }),
  ].filter((group): group is CrmTaskActionGroup => Boolean(group));
  const quickActions = getTaskQuickActions(taskActionGroups);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-[var(--color-foreground)]">{task.title}</p>
          {taskMeta ? (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{taskMeta}</p>
          ) : null}
        </div>
        <a
          href={`/crm/tasks/${encodeURIComponent(task.id)}`}
          className="shrink-0 rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]"
        >
          Open task
        </a>
      </div>
      {guidance ? (
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{guidance}</p>
      ) : null}
      {(contextSignal || slaSignal || completionSummary || evidenceSummary || quickActions.length > 0) ? (
        <div className="mt-3 flex flex-wrap gap-2" aria-label={evidenceLabel}>
          {contextSignal ? (
            <TaskSignal label="Context" value={contextSignal.label} tone={contextSignal.tone} />
          ) : null}
          {slaSignal ? (
            <TaskSignal label="SLA" value={slaSignal.label} tone={slaSignal.tone} />
          ) : null}
          {completionSummary ? (
            <TaskSignal label="Completion" value={completionSummary} tone="default" />
          ) : null}
          {evidenceSummary ? (
            <TaskSignal
              label="Evidence"
              value={`${evidenceSummary.primaryLabel} - ${evidenceSummary.primaryValue}`}
              tone={taskEvidenceTone(evidenceSummary.tone)}
            />
          ) : null}
          {quickActions.map((action) => (
            <TaskActionLink key={`${action.key}:${action.href}`} action={action} />
          ))}
        </div>
      ) : null}
      {contextSignal?.missing.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {contextSignal.missing.map((missing) => (
            <span
              key={missing}
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-0.5 text-[11px] text-[var(--color-muted-foreground)]"
            >
              Missing {formatTaskLabel(missing)}
            </span>
          ))}
        </div>
      ) : null}
      {briefEntries.length > 0 ? (
        <dl className="mt-3 grid gap-2 sm:grid-cols-2">
          {briefEntries.map((entry) => (
            <div
              key={`${entry.label}:${entry.value}`}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1.5"
            >
              <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
                {entry.label}
              </dt>
              <dd className="mt-0.5 text-xs text-[var(--color-foreground)]">{entry.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {escalation ? (
        <p className="mt-2 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-1.5 text-xs text-[var(--color-foreground)]">
          Escalation: {escalation.state === "escalated" ? "Escalated" : "Candidate"}
          {escalation.level ? ` (${formatTaskLabel(escalation.level)})` : ""} - {escalation.reason}
        </p>
      ) : null}
      {automationResolution ? (
        <p className="mt-2 rounded-md border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-2 py-1.5 text-xs text-[var(--color-foreground)]">
          Automation: {automationResolution.title} - {automationResolution.reason}
        </p>
      ) : automationWatch ? (
        <p className="mt-2 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1.5 text-xs text-[var(--color-muted-foreground)]">
          Automation watch: {automationWatch}
        </p>
      ) : null}
      {task.operatorReadModel?.sourceTypeLabel ? (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Source task type: {task.operatorReadModel.sourceTypeLabel}
        </p>
      ) : null}
    </div>
  );
}

function TaskSignal({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: TaskSignalTone;
}) {
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${getTaskSignalClass(tone)}`}>
      {label}: {value}
    </span>
  );
}

function getTaskSignalClass(tone: TaskSignalTone): string {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}

function TaskActionLink({ action }: { action: CrmTaskAction }) {
  const className = [
    "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
    TASK_ACTION_CLASS[action.tone] ?? TASK_ACTION_CLASS.neutral,
  ].join(" ");

  if (action.href.startsWith("/") && !action.href.startsWith("//")) {
    return (
      <a href={action.href} className={className} title={action.description ?? undefined}>
        {action.label}
      </a>
    );
  }

  const opensNewTab = shouldOpenCrmActionHrefInNewTab(action.href);
  return (
    <a
      href={action.href}
      className={className}
      title={action.description ?? undefined}
      target={opensNewTab ? "_blank" : undefined}
      rel={opensNewTab ? "noreferrer" : undefined}
    >
      {action.label}
    </a>
  );
}

function getTaskQuickActions(groups: readonly CrmTaskActionGroup[]): CrmTaskAction[] {
  return groups
    .flatMap((group) => group.actions)
    .filter((action) => !action.disabled)
    .slice(0, 3);
}

function getTaskContextSignal(task: TaskSummary): { label: string; tone: TaskSignalTone; missing: string[] } | null {
  const completeness = task.operatorReadModel?.contextCompleteness;
  if (!completeness) return null;
  const missing = Array.isArray(completeness.missing) ? completeness.missing : [];
  if (!completeness.isUsable) {
    return { label: "Needs repair before staff action", tone: "danger", missing };
  }
  if (missing.length > 0) {
    return { label: "Usable, missing review signals", tone: "warning", missing };
  }
  return { label: "Ready for staff action", tone: "success", missing: [] };
}

function getTaskSlaSignal(
  sla: NonNullable<NonNullable<TaskSummary["operatorReadModel"]>["sla"]>,
): { label: string; tone: TaskSignalTone } {
  const delta = formatTaskSlaMinutes(sla.minutesUntilDue);
  const suffix = sla.status === "overdue" && delta
    ? ` (${delta} overdue)`
    : (sla.status === "due_soon" || sla.status === "scheduled") && delta
      ? ` (in ${delta})`
      : "";

  return {
    label: `${sla.label}${suffix}`,
    tone: sla.severity === "critical"
      ? "danger"
      : sla.severity === "warning"
        ? "warning"
        : sla.severity === "info"
          ? "info"
          : "default",
  };
}

function taskEvidenceTone(tone: CrmTaskProfileSectionTone): TaskSignalTone {
  return tone === "neutral" ? "default" : tone;
}

function formatTaskLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTaskCompletionSummary(task: TaskSummary): string | null {
  const schema = task.operatorReadModel?.completionSchema;
  if (!schema) return null;
  const outcomes = Array.isArray(schema.outcomes) ? schema.outcomes : [];
  const fields = Array.isArray(schema.fields) ? schema.fields : [];
  const outcomeLabel = outcomes.length === 1 ? "1 outcome" : `${outcomes.length} outcomes`;
  const fieldLabel = fields.length === 1 ? "1 field" : `${fields.length} fields`;
  const category = formatTaskLabel(schema.category) ?? "Task";
  const followUpType = formatTaskLabel(schema.defaultFollowUpType) ?? "Follow Up";
  return `${category} - ${outcomeLabel}, ${fieldLabel}, follow-up ${followUpType}`;
}

function getTaskAutomationWatch(task: TaskSummary): string | null {
  if (task.source !== "automation" && !task.sourceRuleId && !task.triggerEventId && !task.triggerType) {
    return null;
  }
  if (task.sourceRuleId) {
    return `Open until source rule ${task.sourceRuleId} is completed, skipped, or closed by a matching CRM event.`;
  }
  if (task.triggerType) {
    return `Open until ${formatTaskLabel(task.triggerType)} is completed, skipped, or superseded.`;
  }
  return "Open until completed, skipped, or closed by a matching CRM event.";
}

function formatTaskSlaMinutes(value: number | null): string | null {
  if (value === null) return null;
  const abs = Math.abs(value);
  if (abs < 60) return `${abs}m`;
  const hours = Math.round(abs / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}
